import React, { useState, useRef } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface GameImageUploadProps {
  onImageChange: (file: File | null) => void;
  currentImageUrl?: string;
  maxSize?: number; // in MB
  className?: string;
}

const GameImageUpload: React.FC<GameImageUploadProps> = ({
  onImageChange,
  currentImageUrl,
  maxSize = 5,
  className = "",
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return `File size must be less than ${maxSize}MB`;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      return 'Please select an image file';
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return 'Supported formats: JPEG, PNG, GIF, WebP';
    }

    return null;
  };

  const handleFileChange = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    onImageChange(file);

    // Create preview URL
    const url = URL.createObjectURL(file);
    
    // Clean up previous preview URL
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    
    setPreviewUrl(url);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const removeImage = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setError(null);
    onImageChange(null);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {previewUrl ? (
        // Image Preview
        <div className="relative group">
          <div className="w-64 h-64 mx-auto rounded-lg overflow-hidden border-2 border-edge bg-surface-subtle">
            <img
              src={previewUrl}
              alt="Game cover preview"
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Overlay with actions */}
          <div className="absolute inset-0 rounded-lg bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={openFileDialog}
                className="p-2 bg-surface rounded-full text-body hover:bg-surface-active transition-colors"
                title="Change image"
              >
                <PhotoIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={removeImage}
                className="p-2 bg-surface rounded-full text-red-600 hover:bg-red-50 transition-colors"
                title="Remove image"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Upload Area
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={openFileDialog}
          className={`relative w-64 h-64 mx-auto border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
            dragActive
              ? 'border-primary-500 bg-primary-50'
              : 'border-edge-input bg-surface-base hover:bg-surface-active'
          }`}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <PhotoIcon className="h-8 w-8 text-disabled mb-2" />
            <span className="text-sm text-muted text-center px-2">
              Click or drag game cover image
            </span>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
      />

      {/* File info and constraints */}
      <div className="text-xs text-muted text-center">
        <p>JPEG, PNG, GIF, WebP up to {maxSize}MB</p>
        <p>Images will be optimized automatically</p>
      </div>

      {/* Error display */}
      {error && (
        <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded p-2 text-center">
          {error}
        </div>
      )}
    </div>
  );
};

export default GameImageUpload;